import { Check, UserPlus } from "lucide-react";
import React, { useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import CreateClientModal from "@/modules/dashboard/clients/components/CreateClientModal";
import {
  canWriteClientAccounts,
  canWriteClients,
} from "@/modules/dashboard/clients/utils/clientsPermissions";
import useTranslations from "@/shared/lang/useTranslations";

import type { RecipientAccountDraft } from "../../utils/recipientAccountDraft";

export interface SaveRecipientActionProps {
  /** The destination that was just paid, already expressed as a client account. */
  draft: RecipientAccountDraft;
  /**
   * The send ended up REJECTED, so the offer is taken off the table.
   *
   * It hides the trigger. It deliberately does NOT unmount a drawer that is already open: the receipt stays
   * live over the socket, so this turns true while somebody may be halfway through the form. The caller used
   * to express the withdrawal by passing no draft at all, which tore this subtree down mid-flow — and what
   * that destroyed was not a button, it was `isOpen` and `clientCreated`. Everything typed went with it, and
   * a client created a second earlier was left behind with no payable account, which is the one state the
   * note below calls worse than never offering. Of the two harms, filing a refused destination is bounded by
   * how fast a person can read; orphaning a client is certain.
   */
  withdrawn?: boolean;
}

/**
 * "Save this recipient" — the offer the receipt makes once the money is gone.
 *
 * It opens the same create-client drawer as the recipient step, with one difference that is the whole point:
 * the account form on its success screen arrives **already filled with the destination that was just paid**.
 * Nobody retypes a key the app watched them use.
 *
 * It is also where the recipient's NAME gets asked. On Bre-B the wizard no longer asks for one — the
 * directory owns it, and what it returns is masked (`Ale*** Pul***`), which is fine to confirm a payment
 * against and useless as a client's name. So the name is typed here, once, by someone who has just decided
 * this is a person worth keeping.
 *
 * Three states, because "saved" is not the only way this ends: the drawer creates the client first and the
 * account second, so closing it in between leaves a client with no payable account. Saying so is cheap;
 * re-offering the button there would quietly create the same person twice.
 *
 * **The outcome is resolved when the drawer CLOSES, never on the callback that reports progress inside it.**
 * `CreateClientModal` fires `onClientCreated` the instant the client exists and then deliberately stays open
 * on its success step — which is exactly where the prefilled account form lives. Swapping this subtree for
 * the outcome line at that moment unmounted the drawer before that step could paint, so the account form
 * never rendered, `onAccountCreated` became unreachable, `receipt_save_recipient_done` was dead copy in all
 * three languages, and EVERY run of this feature ended in the one state the comment below calls worse than
 * never offering: a client with no payable account. The feature was inert from the day it shipped, and it is
 * the kind of inert that looks like it worked.
 */
export const SaveRecipientAction: React.FC<SaveRecipientActionProps> = ({ draft, withdrawn = false }) => {
  const { user } = useAuth();
  const { t } = useTranslations("dispersions");
  const [isOpen, setIsOpen] = useState(false);
  const [clientCreated, setClientCreated] = useState(false);
  const [accountSaved, setAccountSaved] = useState(false);

  // BOTH permissions, because the offer is the account and not the client. `CreateClientModal` gates its
  // account step on `canWriteClientAccounts`, and `clients:write` does not imply it — so someone holding
  // only the first one pressed "Save this recipient", created a client, and was told its account could not
  // be saved and to add it from Clientes, which they cannot do either. What is left behind is a client with
  // no payable account: worse than never offering.
  if (!canWriteClients(user) || !canWriteClientAccounts(user)) return null;

  // Only once the drawer is shut AND something was actually created. A close with nothing created returns
  // the button, so a mis-click costs nothing.
  if (!isOpen && clientCreated) {
    return (
      <p className="inline-flex max-w-[360px] items-start gap-1.5 text-sm text-muted-foreground">
        {accountSaved && <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />}
        <span>{t(accountSaved ? "receipt_save_recipient_done" : "receipt_save_recipient_partial")}</span>
      </p>
    );
  }

  // Withdrawn with nothing in flight — the offer is simply not made. Reaching here means no client exists
  // either, since the branch above claims that case, so there is nothing left to finish or to report.
  if (withdrawn && !isOpen) return null;

  return (
    <>
      {/* Hidden once the send is refused, while the drawer below stays mounted if it is already open. */}
      {!withdrawn && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-md text-sm font-semibold text-primary transition-colors hover:text-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <UserPlus className="size-4" aria-hidden="true" />
          {t("receipt_save_recipient")}
        </button>
      )}
      <CreateClientModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        clientOnly
        successAccountCurrency={draft.currency}
        successAccountPrefill={{ bank: draft.bank, methodFields: draft.methodFields }}
        // Both record progress and neither resolves the UI — the drawer stays mounted until it is closed.
        onClientCreated={() => setClientCreated(true)}
        onAccountCreated={() => {
          setClientCreated(true);
          setAccountSaved(true);
        }}
      />
    </>
  );
};
